/**
 * Priority friends — the trainer's inner circle (Pro).
 *
 * A Pro trainer pins up to THREE friends on Friends > Manage. Those friends are then
 * prioritised on the Profile page: the "Friends are looking for:" strip probes them
 * first and shows their leads ahead of everyone else's, so the profile answers
 * "what should I catch today for the people I actually trade with" rather than
 * drawing from the whole friends list at random.
 *
 * The pinned ids live in the account's `UserPriorities` blob, which already
 * syncs cross-device via /api/priorities (the server schema is `.passthrough()`,
 * so no new table or migration is needed). Older accounts have no key at all,
 * which is why every read goes through `priorityFriendIds()`.
 *
 * Pure + storage-agnostic so it is unit-testable without a DOM or the network.
 */
import type { UserPriorities } from "./userPriorities";

/** How many friends a Pro trainer can pin. */
export const MAX_PRIORITY_FRIENDS = 3;

/**
 * Sanitise a raw ids array: positive integers only, de-duped, capped at three,
 * order preserved (the order the trainer picked them in is the display order).
 */
export function sanitizePriorityFriendIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  const seen = new Set<number>();
  for (const value of raw) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 1) continue;
    const id = Math.trunc(value);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_PRIORITY_FRIENDS) break;
  }
  return out;
}

/** The pinned friend ids on a priorities object. Always an array. */
export function priorityFriendIds(priorities: UserPriorities | null | undefined): number[] {
  return sanitizePriorityFriendIds(priorities?.priorityFriendIds);
}


export type TogglePriorityResult =
  | { ok: true; ids: number[]; pinned: boolean }
  | { ok: false; reason: "full"; ids: number[] };

/**
 * Pin / unpin a friend. Unpinning always succeeds; pinning fails once three are
 * already pinned so the caller can surface a toast rather than silently
 * dropping the choice.
 */
export function togglePriorityFriend(ids: readonly number[], friendId: number): TogglePriorityResult {
  const current = sanitizePriorityFriendIds(ids as number[]);
  if (current.includes(friendId)) {
    return { ok: true, ids: current.filter((id) => id !== friendId), pinned: false };
  }
  if (current.length >= MAX_PRIORITY_FRIENDS) {
    return { ok: false, reason: "full", ids: current };
  }
  return { ok: true, ids: [...current, friendId], pinned: true };
}

/**
 * Drop ids that are no longer friends (removed friendship, declined request).
 * Called with the live friends list so a stale pin never occupies one of the
 * three slots forever. Returns the SAME array reference when nothing changed so
 * callers can skip a redundant write.
 */
export function prunePriorityFriendIds(
  ids: readonly number[],
  liveFriendIds: readonly number[],
): number[] {
  const live = new Set(liveFriendIds);
  const kept = ids.filter((id) => live.has(id));
  return kept.length === ids.length ? (ids as number[]) : kept;
}

/**
 * Reorder a friends list so pinned friends come first, in pin order, with the
 * rest following in their original order. Used to bound-and-bias the per-friend
 * dex fan-out: a pinned friend must never fall outside the probe cap.
 */
export function orderByPriority<T>(
  items: readonly T[],
  idOf: (item: T) => number,
  ids: readonly number[],
): T[] {
  const rank = new Map<number, number>();
  ids.forEach((id, i) => rank.set(id, i));
  return items
    .map((item, index) => ({ item, index, rank: rank.get(idOf(item)) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((e) => e.item);
}

/**
 * Split a ranked pool into [pinned-friend entries, everyone else], each keeping
 * the pool's own ranking. The Profile strip shows the pinned leads first and
 * fills any remaining slots from the rest, so pinning a friend guarantees their
 * lead is on screen without hiding the wider pool when they need nothing.
 */
export function partitionByPriority<T>(
  pool: readonly T[],
  friendIdOf: (item: T) => number,
  ids: readonly number[],
): { pinned: T[]; rest: T[] } {
  const set = new Set(ids);
  const pinned: T[] = [];
  const rest: T[] = [];
  for (const item of pool) {
    if (set.has(friendIdOf(item))) pinned.push(item);
    else rest.push(item);
  }
  return { pinned, rest };
}

/**
 * The friend's display name, in the precedence the wish list / compare / trade
 * surfaces already use: their Pokémon GO trainer name first, then whatever
 * profile name we have, then the account handle.
 */
export function friendDisplayName(friend: {
  username?: string | null;
  displayName?: string | null;
  trainerName?: string | null;
} | null | undefined): string {
  return friend?.trainerName || friend?.displayName || friend?.username || "Trainer";
}
